import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import { useDispatch, useSelector } from "react-redux";
import MainLayout from "../../components/layout/MainLayout";
import Breadcrumbs from "../../components/common/Breadcrumbs";
import ProgressNotePrpForm from "./ProgressNotePrpForm";
import {
  fetchProgressNotePrp,
  updateProgressNotePrp,
  clearCurrentProgressNotePrp,
} from "../../store/slices/progressNotePrpSlice";
import { fetchPatient } from "../../store/slices/patientsSlice";

const EditProgressNotePrp = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const { state } = useLocation();
  const { caseId, patientId } = state || {};

  const { currentProgressNotePrp, isLoading, error } = useSelector(
    (state) => state.progressNotePrp
  );

  const [formData, setFormData] = useState({
    patient_id: patientId || "",
    case_id: caseId || "",
    details: [],
  });

  const [patientData, setPatientData] = useState(null);

  useEffect(() => {
    if (id) {
      dispatch(fetchProgressNotePrp(id));
    }
    return () => {
      dispatch(clearCurrentProgressNotePrp());
    };
  }, [dispatch, id]);

  useEffect(() => {
    if (currentProgressNotePrp && currentProgressNotePrp.id === parseInt(id)) {
      setFormData({
        patient_id: currentProgressNotePrp.patient_id || "",
        case_id: currentProgressNotePrp.case_id || "",
        details: currentProgressNotePrp.details || [],
      });

      // Fetch patient details
      if (currentProgressNotePrp.patient_id) {
        dispatch(fetchPatient(currentProgressNotePrp.patient_id))
          .unwrap()
          .then((patient) => {
            setPatientData(patient);
          })
          .catch((err) => console.error("Failed to fetch patient details:", err));
      }
    }
  }, [currentProgressNotePrp, id, dispatch]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.details || formData.details.length === 0) {
      Swal.fire({
        title: "Validation Error",
        text: "Please add at least one session note.",
        icon: "warning",
      });
      return;
    }

    // Validate date fields
    const missingDate = formData.details.some(d => !d.session_date);
    if (missingDate) {
      Swal.fire({
        title: "Validation Error",
        text: "Please specify the session date for all entries.",
        icon: "warning",
      });
      return;
    }

    try {
      await dispatch(updateProgressNotePrp({ id, formData })).unwrap();

      Swal.fire({
        title: "Success!",
        text: "Progress Notes PRP updated successfully.",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });

      // Stay on edit page
    } catch (err) {
      let errorMessage = "Failed to update Progress Notes PRP";
      if (typeof err === "string") {
        errorMessage = err;
      } else if (typeof err === "object" && err !== null) {
        errorMessage = Object.values(err).flat().join("\n");
      }
      Swal.fire({
        title: "Error",
        text: errorMessage,
        icon: "error",
      });
    }
  };

  return (
    <MainLayout>
      <div className="container-xxl flex-grow-1 container-p-y">
        <Breadcrumbs />

        {/* Patient Summary Header Card */}
        {patientData && (
          <div className="card mb-4">
            <div className="card-body bg-light-primary rounded-4">
              <div className="row g-3">
                <div className="col-md-3">
                  <span className="fw-semibold text-secondary small d-block">Client Name:</span>
                  <span className="fw-bold text-dark">{patientData.first_name} {patientData.last_name}</span>
                </div>
                <div className="col-md-3">
                  <span className="fw-semibold text-secondary small d-block">MR Number:</span>
                  <span className="fw-bold text-dark">{patientData.mr_no || "N/A"}</span>
                </div>
                <div className="col-md-3">
                  <span className="fw-semibold text-secondary small d-block">Date of Birth:</span>
                  <span className="fw-bold text-dark">
                    {patientData.dob ? new Date(patientData.dob).toLocaleDateString("en-GB") : "N/A"}
                  </span>
                </div>
                <div className="col-md-3">
                  <span className="fw-semibold text-secondary small d-block">Gender:</span>
                  <span className="fw-bold text-dark capitalize">{patientData.gender || "N/A"}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="card">
          <div className="card-header border-bottom">
            <h5 className="mb-0">Edit Progress Notes PRP</h5>
          </div>
          <div className="card-body">
            {isLoading ? (
              <div className="d-flex justify-content-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : error ? (
              <div className="alert alert-danger m-4">{error}</div>
            ) : (
              <form onSubmit={handleSubmit}>
                <ProgressNotePrpForm
                  formData={formData}
                  setFormData={setFormData}
                />
                <div className="row px-4 pb-4">
                  <div className="col-md-2 ms-auto">
                    <button type="submit" className="btn btn-primary w-100 sticky-form-submit">
                      <i className="ri-save-3-line me-1"></i> Update
                    </button>
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default EditProgressNotePrp;
